import React, { JSX } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import { Resume } from 'types/types';
import { formatDate } from 'utils/formatDate';
import { ResumeViewerToolbar } from './ResumeViewerToolbar';
import { SortField, SortOrder } from './resumeViewerTypes';

interface ResumeViewerTableProps {
  resumes: Resume[];
  selectedResume: Resume | null;
  sortField: SortField;
  sortOrder: SortOrder;
  searchQuery: string;
  title: string;
  isMobile: boolean;
  isSmall: boolean;
  onSelect: (resume: Resume) => void;
  onSort: (field: SortField) => void;
  onSortSelect: (field: SortField, order: SortOrder) => void;
  onSearchChange: (query: string) => void;
}

export function ResumeViewerTable(props: ResumeViewerTableProps): JSX.Element {
  return (
    <Paper
      elevation={props.isMobile ? 0 : 1}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        ...(props.isMobile
          ? { width: '100%', boxShadow: 'none', backgroundColor: 'transparent' }
          : { width: '50%' }),
      }}
    >
      <ResumeViewerToolbar
        count={props.resumes.length}
        title={props.title}
        isMobile={props.isMobile}
        isSmall={props.isSmall}
        sortField={props.sortField}
        sortOrder={props.sortOrder}
        searchQuery={props.searchQuery}
        onSortSelect={props.onSortSelect}
        onSearchChange={props.onSearchChange}
      />
      <TableContainer
        sx={{
          flex: 1,
          overflow: 'auto',
          '& .MuiTable-root': { tableLayout: props.isMobile ? 'fixed' : 'auto' },
        }}
      >
        <Table stickyHeader size="small">
          <ResumeTableHead
            isMobile={props.isMobile}
            onSort={props.onSort}
            getSortIcon={field => getSortIcon(field, props.sortField, props.sortOrder)}
          />
          <TableBody>
            {props.resumes.map(resume => (
              <ResumeTableRow
                key={resume.id}
                resume={resume}
                selectedResume={props.selectedResume}
                isMobile={props.isMobile}
                isSmall={props.isSmall}
                onSelect={props.onSelect}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function ResumeTableHead(props: {
  isMobile: boolean;
  onSort: (field: SortField) => void;
  getSortIcon: (field: SortField) => JSX.Element;
}): JSX.Element {
  const headerCellSx = {
    cursor: 'pointer',
    userSelect: 'none',
    py: props.isMobile ? 0.25 : 0.5,
    px: props.isMobile ? 0.5 : 1,
    width: props.isMobile ? '35%' : 'auto',
    backgroundColor: 'background.paper',
  };

  return (
    <TableHead>
      <TableRow>
        <HeaderCell
          field="candidateId"
          label="Candidate"
          icon={<PersonIcon fontSize={props.isMobile ? 'small' : 'medium'} />}
          sx={headerCellSx}
          onSort={props.onSort}
          getSortIcon={props.getSortIcon}
        />
        <HeaderCell
          field="jobId"
          label="Job"
          icon={<WorkIcon fontSize={props.isMobile ? 'small' : 'medium'} />}
          sx={headerCellSx}
          onSort={props.onSort}
          getSortIcon={props.getSortIcon}
        />
        {!props.isMobile && (
          <HeaderCell
            field="updatedAt"
            label="Updated"
            icon={<ScheduleIcon fontSize="medium" />}
            sx={{
              cursor: 'pointer',
              userSelect: 'none',
              py: 0.5,
              px: 1,
              backgroundColor: 'background.paper',
            }}
            onSort={props.onSort}
            getSortIcon={props.getSortIcon}
          />
        )}
      </TableRow>
    </TableHead>
  );
}

function HeaderCell(props: {
  field: SortField;
  label: string;
  icon: JSX.Element;
  sx: object;
  onSort: (field: SortField) => void;
  getSortIcon: (field: SortField) => JSX.Element;
}): JSX.Element {
  return (
    <TableCell sx={props.sx} onClick={() => props.onSort(props.field)}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {props.icon}
        <Typography variant="caption" fontWeight="bold" noWrap>
          {props.label}
        </Typography>
        {props.getSortIcon(props.field)}
      </Box>
    </TableCell>
  );
}

function ResumeTableRow(props: {
  resume: Resume;
  selectedResume: Resume | null;
  isMobile: boolean;
  isSmall: boolean;
  onSelect: (resume: Resume) => void;
}): JSX.Element {
  const { resume, selectedResume, isMobile, isSmall, onSelect } = props;

  return (
    <TableRow
      hover
      selected={selectedResume?.id === resume.id}
      onClick={() => onSelect(resume)}
      sx={{
        cursor: 'pointer',
        height: isMobile ? 48 : 'auto',
        '&.Mui-selected': { backgroundColor: 'action.selected' },
        '&:hover': { backgroundColor: 'action.hover' },
      }}
    >
      <TableCell sx={cellSx(isMobile)}>
        <Typography
          variant={bodyVariant(isMobile)}
          fontWeight="medium"
          noWrap
          sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
        >
          {resume.candidate?.fullName || resume.candidateId}
        </Typography>
        {isMobile && (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ display: 'block', fontSize: '0.7rem' }}
          >
            {formatDate(resume.updatedAt, isMobile, isSmall)}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={cellSx(isMobile)}>
        <Typography
          variant={bodyVariant(isMobile)}
          fontWeight="medium"
          noWrap
          sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
        >
          {resume.job?.title || 'Unknown Job'}
        </Typography>
        {!isMobile && resume.job?.company && (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ display: 'block', fontSize: '0.7rem' }}
          >
            {resume.job.company}
          </Typography>
        )}
      </TableCell>
      {!isMobile && (
        <TableCell sx={{ py: 0.5, px: 1 }}>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            {formatDate(resume.updatedAt, isMobile, isSmall)}
          </Typography>
          {resume.createdAt && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', fontSize: '0.7rem' }}
            >
              Created: {formatDate(resume.createdAt, isMobile, isSmall)}
            </Typography>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}

function getSortIcon(field: SortField, sortField: SortField, sortOrder: SortOrder): JSX.Element {
  if (sortField !== field) return <></>;
  return sortOrder === 'asc' ? (
    <ArrowUpIcon fontSize="small" />
  ) : (
    <ArrowDownIcon fontSize="small" />
  );
}

function cellSx(isMobile: boolean): object {
  return { py: isMobile ? 0.25 : 0.5, px: isMobile ? 0.5 : 1, overflow: 'hidden' };
}

function bodyVariant(isMobile: boolean): 'caption' | 'body2' {
  return isMobile ? 'caption' : 'body2';
}
